import React, { useMemo } from 'react'
import {
  PiHouseFill,
  PiHouse,
  PiMegaphoneFill,
  PiMegaphone,
  PiHandCoinsFill,
  PiHandCoins,
  PiSealPercentFill,
  PiSealPercent,
  PiHandshakeFill,
  PiHandshake,
  PiChartLineUpFill,
  PiChartLineUp,
  PiGearFill,
  PiGear,
  PiQuestionFill,
  PiQuestion,
  PiSignOutFill,
  PiSignOut,
  PiListFill,
  PiList,
  PiBellFill,
  PiBell,
} from 'react-icons/pi'
import {
  TargetArrowIcon,
  TargetArrowOutlineIcon,
  WorkspaceFilledIcon,
  WorkspaceOutlineIcon,
} from './nav-icons'
import { NavTooltip } from './NavTooltip'
import { cn } from 'components/UI/cn'
import type { NavIconType } from './types'

/**
 * A reusable icon button component for the left navigation bar.
 *
 * Features:
 * - Hover and selection states with consistent styling
 * - Dynamic icon selection based on state (filled vs outline)
 * - Optional text label display below icon (FIR-4955)
 * - Integration with navigation tooltip (when label is hidden)
 *
 * Layout Specs:
 * - Button: 44x44px with 8px border radius
 * - Label (when shown): Inter Medium 9px, 16px line-height, 0px gap from icon
 * - Spacing: 20px gap between nav items (controlled by parent)
 *
 * @example
 * // With visible label
 * <LeftNavIconButton
 *   icon="target"
 *   label="Targets"
 *   isSelected={true}
 *   showLabel={true}
 *   onClick={handleClick}
 * />
 *
 * @example
 * // With tooltip only (default)
 * <LeftNavIconButton
 *   icon="deals"
 *   label="Deals"
 *   onClick={handleClick}
 * />
 */
interface LeftNavIconButtonProps {
  icon: NavIconType
  label: string
  isSelected?: boolean
  isSecondaryActive?: boolean // Maintains hover/active state when this section's slide-out is open
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  className?: string
  showLabel?: boolean // When true, displays text label below icon; when false, shows tooltip on hover
}

const ICON_SIZE = 24

export const LeftNavIconButton: React.FC<LeftNavIconButtonProps> = ({
  icon,
  label,
  isSelected = false,
  isSecondaryActive = false,
  onClick,
  className,
  showLabel = false,
}) => {
  const IconComponent = useMemo(() => {
    const iconMap: Record<
      NavIconType,
      { filled: React.ReactNode; outline: React.ReactNode }
    > = {
      house: {
        filled: <PiHouseFill size={ICON_SIZE} />,
        outline: <PiHouse size={ICON_SIZE} />,
      },
      target: {
        filled: <TargetArrowIcon size={ICON_SIZE} />,
        outline: <TargetArrowOutlineIcon size={ICON_SIZE} />,
      },
      megaphone: {
        filled: <PiMegaphoneFill size={ICON_SIZE} />,
        outline: <PiMegaphone size={ICON_SIZE} />,
      },
      'hand-coins': {
        filled: <PiHandCoinsFill size={ICON_SIZE} />,
        outline: <PiHandCoins size={ICON_SIZE} />,
      },
      'seal-percent': {
        filled: <PiSealPercentFill size={ICON_SIZE} />,
        outline: <PiSealPercent size={ICON_SIZE} />,
      },
      handshake: {
        filled: <PiHandshakeFill size={ICON_SIZE} />,
        outline: <PiHandshake size={ICON_SIZE} />,
      },
      'chart-line-up': {
        filled: <PiChartLineUpFill size={ICON_SIZE} />,
        outline: <PiChartLineUp size={ICON_SIZE} />,
      },
      workspace: {
        filled: <WorkspaceFilledIcon size={ICON_SIZE} />,
        outline: <WorkspaceOutlineIcon size={ICON_SIZE} />,
      },
      gear: {
        filled: <PiGearFill size={ICON_SIZE} />,
        outline: <PiGear size={ICON_SIZE} />,
      },
      question: {
        filled: <PiQuestionFill size={ICON_SIZE} />,
        outline: <PiQuestion size={ICON_SIZE} />,
      },
      'sign-out': {
        filled: <PiSignOutFill size={ICON_SIZE} />,
        outline: <PiSignOut size={ICON_SIZE} />,
      },
      list: {
        filled: <PiListFill size={ICON_SIZE} />,
        outline: <PiList size={ICON_SIZE} />,
      },
      bell: {
        filled: <PiBellFill size={ICON_SIZE} />,
        outline: <PiBell size={ICON_SIZE} />,
      },
    }

    return isSelected ? iconMap[icon].filled : iconMap[icon].outline
  }, [icon, isSelected])

  // Hover is handled via CSS in main.css ([data-left-nav] button:hover)
  // This works around Bootstrap's button resets overriding Tailwind hover classes
  const buttonElement = (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center justify-center w-[44px] h-[44px] rounded-lg border-none text-white cursor-pointer transition-colors duration-150',
        isSelected || isSecondaryActive
          ? 'bg-[var(--nav-bg-hover)]'
          : 'bg-transparent',
        className
      )}
      aria-label={label}
    >
      {IconComponent}
    </button>
  )

  if (showLabel) {
    // When showing label, wrap button and label in a container with no gap (0px between icon and label per Figma)
    return (
      <div className="flex flex-col items-center w-full">
        {buttonElement}
        <span className="text-white text-[9px] font-medium h-4 flex items-center justify-center w-full text-center leading-[16px]">
          {label}
        </span>
      </div>
    )
  }

  return <NavTooltip label={label}>{buttonElement}</NavTooltip>
}
